/**
 * Reading errors thrown by the broadcasts service.
 *
 * `wrapCommsError` (src/service/comms/_error.js) hands every caller a plain
 * `Error` carrying `.status`, `.data` and `.cause`. That is deliberately raw —
 * it preserves the response instead of interpreting it. This module is the
 * interpretation half: status in, spec copy out.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE IS AND IS NOT
 * ---------------------------------------------------------------------------
 * Pure and read-only. It never throws, never logs and never re-wraps: it is
 * called from render paths and from catch blocks, and an error reader that can
 * itself fail is worse than no reader at all.
 *
 * It contains no field-name literals. `fieldErrorsFromError` returns wire field
 * names, but every one of them is read out of the response at runtime — the
 * server names the field, this file only relays it.
 */

/** FastAPI's location prefix for a request-body error. Not a field name. */
const BODY_LOC = "body";

/** Separator between stacked validation messages. */
const DETAIL_JOINER = " · ";

/**
 * The last meaningful segment of a FastAPI `loc` tuple.
 *
 * `loc` is a path, not a name: `["body", "notification_title"]` locates a field
 * inside the request body. Dropping the container leaves the part the author
 * can actually act on.
 *
 * @param {any} loc
 * @returns {string|null}
 */
const fieldFromLoc = (loc) => {
  if (!Array.isArray(loc)) return null;
  const segments = loc.filter((part) => part !== BODY_LOC && part !== null && part !== undefined);
  const last = segments[segments.length - 1];
  return last === undefined ? null : String(last);
};

/**
 * The human-readable detail on an error, if there is one.
 *
 * FastAPI sends `detail` two ways: a plain string for a deliberate rejection,
 * and an array of `{ loc, msg }` objects for schema validation. `_error.js`
 * only trusts the string form, so the array form never reaches `err.message` —
 * this is where it gets read.
 *
 * @param {Error & { data?: any }} err
 * @returns {string|null} Null when there is nothing a human could read.
 */
const detailFromError = (err) => {
  const detail = err?.data?.detail;

  if (typeof detail === "string") return detail.trim() ? detail : null;

  if (Array.isArray(detail)) {
    const lines = detail
      .map((entry) => {
        const message = typeof entry?.msg === "string" ? entry.msg.trim() : "";
        if (!message) return null;
        const field = fieldFromLoc(entry?.loc);
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    return lines.length > 0 ? lines.join(DETAIL_JOINER) : null;
  }

  // A bare object is FastAPI shape we do not recognise. Rendering
  // "[object Object]" at the user is worse than saying nothing.
  return null;
};

/**
 * Per-field validation messages, keyed by the wire field name.
 *
 * Lets the composer put the message next to the input that caused it instead of
 * dropping one long string above the form. Only the FIRST message per field is
 * kept — a field with three complaints still only has one place to show them,
 * and the first is the one the server checked first.
 *
 * @param {Error & { data?: any }} err
 * @returns {Object<string, string>} Empty when the error is not field-shaped.
 */
export const fieldErrorsFromError = (err) => {
  const detail = err?.data?.detail;
  if (!Array.isArray(detail)) return {};

  return detail.reduce((fields, entry) => {
    const field = fieldFromLoc(entry?.loc);
    const message = typeof entry?.msg === "string" ? entry.msg.trim() : "";
    if (!field || !message || fields[field]) return fields;
    return { ...fields, [field]: message };
  }, {});
};

/* ── Status predicates ────────────────────────────────────────────────── */

/** Manager-only surface, and this account is not one. */
export const isForbidden = (err) => err?.status === 403;

/** No such broadcast — or none this tenant may know about. See `messageFor`. */
export const isNotFound = (err) => err?.status === 404;

/** The payload was rejected by the schema; `fieldErrorsFromError` has details. */
export const isValidation = (err) => err?.status === 422;

/* ── Copy ─────────────────────────────────────────────────────────────── */

/**
 * The message to show for a failed broadcasts call.
 *
 * The 404 wording is load-bearing. The API answers 404, not 403, when a
 * broadcast belongs to another tenant — the absence of the record is the whole
 * point. Softening this into a permission message would confirm the broadcast
 * exists to someone who is not allowed to know that, so it must never mention
 * permission or access.
 *
 * @param {Error & { status?: number, data?: any }} err
 * @returns {string} Always a non-empty string; this is the last stop before UI.
 */
export const messageFor = (err) => {
  switch (err?.status) {
    case 403:
      return "You need manager access to view broadcasts.";
    case 404:
      return "Broadcast not found.";
    case 422:
      // The server's field-level complaint beats our generic line whenever it
      // exists — it names what to fix.
      return detailFromError(err) || "That broadcast was rejected. Check the highlighted fields.";
    case 423:
      return "This broadcast is locked and can no longer be changed.";
    default: {
      const message = err?.message;
      if (typeof message === "string" && message.trim()) return message;
      return "Something went wrong loading broadcasts.";
    }
  }
};
